﻿import {ensureErrorType} from "@2d-front-monorepo/core";
import {DependencyList, useEffect, useState} from "react";

export enum RemoteDataStatus {
	Loading,
	Ok,
	Error
}

export type RemoteData<T> =
	| { status: RemoteDataStatus.Loading; data: T | undefined; error: undefined }
	| { status: RemoteDataStatus.Ok; data: T; error: undefined }
	| { status: RemoteDataStatus.Error; data: undefined; error: Error }
	;

interface ErrorOrData<T> {
	data: T | undefined;
	error: Error | undefined;
}

export function useRemoteData<T>(factory: (signal: AbortSignal) => Promise<T>, deps: DependencyList): RemoteData<T> {
	const [data, setData] = useState<ErrorOrData<T> | undefined>(undefined);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const controller = new AbortController();
		setLoading(true);

		factory(controller.signal).then(result => {
			if (!controller.signal.aborted) {
				setData({data: result, error: undefined});
				setLoading(false);
			}
		}, err => {
			if (!controller.signal.aborted) {
				setData({data: undefined, error: ensureErrorType(err)});
				setLoading(false);
			}
		});

		// Abort on cleanup so a superseded (deps changed) or discarded (StrictMode
		// double-mount / unmount) request neither hits the network nor writes stale state.
		return () => controller.abort();
	}, deps);

	if (loading) {
		return {status: RemoteDataStatus.Loading, data: data?.data, error: undefined};
	} else {
		if (data?.error) {
			return {status: RemoteDataStatus.Error, data: undefined, error: data.error};
		}

		return {status: RemoteDataStatus.Ok, data: data?.data as T, error: undefined};
	}
}
